import type {
  CreateDeckRequest,
  CreateShareOptions,
  Deck,
  DeckAnalytics,
  GalleryImage,
  GenerateImageRequest,
  GenerateImageResponse,
  GenerateRequest,
  GenerateResponse,
  ImagePromptRequest,
  ImagePromptResponse,
  Share,
  ShareEvent,
  ShareResult,
  SharedDeck,
  ShareSummary,
  ShareTtl,
  UpdateDeckRequest,
  EvalRun,
  ChatRequest,
  ChatEnvelope,
  StreamEvent,
  DeckSource,
  UploadSourceRequest,
  Folder,
  MessageFeedbackRequest,
  VisualIssue,
  VisualCheckResponse,
} from './types';
import type { DeckModel } from '../lib/deck-model';
import { getToken } from '../lib/auth';

// In dev Vite proxies /api → :8080. In prod the Go binary serves the SPA
// and the API from the same origin. Either way an empty base prefix works.
const BASE = '';

/**
 * An HTTP error carrying the response status so callers can branch on it
 * (e.g. the share view distinguishing 401 "wrong passcode" from 410 "expired").
 * Replaces the bare `new Error("${status} ${statusText}")` the wrappers threw
 * before sharing v2 — that lost the status to a string and made branching
 * impossible.
 */
export class HttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  constructor(status: number, statusText: string) {
    super(`${status} ${statusText}`);
    this.name = 'HttpError';
    this.status = status;
    this.statusText = statusText;
  }
}

/** Authenticated request — attaches `Authorization: Bearer ${token}` when
 *  a token is available. Used for every `/api/decks/*` route. */
async function httpAuth<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BASE + path, { ...init, headers });
  if (!res.ok) {
    throw new HttpError(res.status, res.statusText);
  }
  // 204 No Content responses (e.g. DELETE share) have no body to decode.
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

/** Anonymous request — no Authorization header. Used for `/api/shares/:token`
 *  so deck recipients without Ricoh accounts can still load shared decks.
 *  Throws an {@link HttpError} (with a `.status`) on a non-2xx response so the
 *  share view can branch on 401 / 404 / 410. */
async function httpAnon<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    throw new HttpError(res.status, res.statusText);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

export const api = {
  listDecks:   (): Promise<Deck[]>   => httpAuth<Deck[]>('/api/decks'),
  getDeck:     (id: string): Promise<Deck>  => httpAuth<Deck>(`/api/decks/${encodeURIComponent(id)}`),
  /** Create a deck. The FE has already compiled the model -> html via
   *  render-deck.ts; both the compiled html and the authoritative model are
   *  sent. The server stores both, validates html, and stamps id/timestamps.
   *  Returns the created Deck (with its server-assigned id). */
  createDeck:  (req: CreateDeckRequest): Promise<Deck> =>
    httpAuth<Deck>('/api/decks', {
      method: 'POST',
      // The whole typed request is serialised; optional `status` + `conversation`
      // are forwarded only when present (JSON.stringify drops undefined fields),
      // so an unset status keeps the wire body to the V1 shape and lets the
      // server default it to "saved".
      body: JSON.stringify(req),
    }),
  /** Full-replace update of an existing deck (owner-only). PUTs the entire deck
   *  body; the server preserves the original createdAt + ownerId and overwrites
   *  everything else, then returns the updated Deck. The id is sent in the URL,
   *  not the body. Mirrors createDeck's authed wrapper. */
  updateDeck:  (id: string, body: UpdateDeckRequest): Promise<Deck> =>
    httpAuth<Deck>(`/api/decks/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  /** Delete a deck by id (owner-only). 204 No Content on success; the helper
   *  returns void rather than the empty body. 404 (missing or not-owner)
   *  surfaces as an HttpError the caller can branch on by `.status`. */
  deleteDeck:  (id: string): Promise<void> =>
    httpAuth<void>(`/api/decks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Ask the server to generate a DeckModel from a prompt. The server calls
   *  Claude on Vertex AI and returns ONLY the model (it never compiles); the
   *  caller compiles to html via renderDeck(). On refine (follow-on) calls the
   *  request also carries `history` + `currentModel` so the model keeps context
   *  — both are forwarded only when present so first-gen bodies stay minimal. */
  generateDeck: async (req: GenerateRequest): Promise<DeckModel> => {
    const body: GenerateRequest = { prompt: req.prompt };
    if (req.slideCount !== undefined) body.slideCount = req.slideCount;
    if (req.tone !== undefined) body.tone = req.tone;
    if (req.quality !== undefined) body.quality = req.quality;
    if (req.history && req.history.length > 0) body.history = req.history;
    if (req.currentModel) body.currentModel = req.currentModel;
    const res = await httpAuth<GenerateResponse>('/api/decks/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return res.model;
  },
  /** One AI Studio co-pilot turn. Sends the chat thread (+ the current deck for
   *  refine, + a deckId so the server folds in uploaded sources) and returns the
   *  typed envelope: a message, widgets, and — when the co-pilot produced one —
   *  the full schemaVersion-2 deck. */
  chatDeck: (req: ChatRequest): Promise<ChatEnvelope> =>
    httpAuth<ChatEnvelope>('/api/decks/chat', {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  /** Streaming co-pilot turn (SSE). Emits incremental events: a `plan` of the
   *  slides a multi-slide refine will touch, a `slide` per sub-agent edit as it
   *  lands, or one `envelope` for a non-fan-out turn, then `done`. onEvent fires
   *  per event so the Studio can shimmer + patch slides live. */
  chatDeckStream: async (req: ChatRequest, onEvent: (ev: StreamEvent) => void, signal?: AbortSignal): Promise<void> => {
    const token = await getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(BASE + '/api/decks/chat/stream', { method: 'POST', headers, body: JSON.stringify(req), signal });
    if (!res.ok || !res.body) throw new HttpError(res.status, res.statusText);
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
        const line = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!line) continue;
        try { onEvent(JSON.parse(line.slice(5).trim()) as StreamEvent); } catch { /* skip malformed frame */ }
      }
    }
  },
  /** Inline "Ask AI" rewrite of a single slide text region. Returns the new text. */
  editRegion: async (text: string, instruction: string): Promise<string> => {
    const res = await httpAuth<{ text: string }>('/api/decks/edit-region', {
      method: 'POST', body: JSON.stringify({ text, instruction }),
    });
    return res.text;
  },
  /** List a deck's uploaded source documents (owner-only). */
  listSources: (deckId: string): Promise<DeckSource[]> =>
    httpAuth<DeckSource[]>(`/api/decks/${encodeURIComponent(deckId)}/sources`),
  /** Upload a source document (base64) to ground a deck's generation. */
  uploadSource: (deckId: string, body: UploadSourceRequest): Promise<DeckSource> =>
    httpAuth<DeckSource>(`/api/decks/${encodeURIComponent(deckId)}/sources`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Delete a source document by id (owner-only). */
  deleteSource: (deckId: string, sid: string): Promise<void> =>
    httpAuth<void>(`/api/decks/${encodeURIComponent(deckId)}/sources/${encodeURIComponent(sid)}`, {
      method: 'DELETE',
    }),
  // ── resumable AI-Studio chats (owner-scoped, one current thread per deck) ──
  /** Load the deck's current chat thread (messages: [] when none saved yet). */
  getChat: (deckId: string): Promise<{ id: string; title?: string; messages: unknown[] }> =>
    httpAuth(`/api/decks/${encodeURIComponent(deckId)}/chats`),
  /** Upsert the deck's current chat thread (the full message list). */
  saveChat: (deckId: string, messages: unknown[], title?: string): Promise<void> =>
    httpAuth<void>(`/api/decks/${encodeURIComponent(deckId)}/chats`, {
      method: 'POST', body: JSON.stringify({ messages, title }),
    }),
  /** Clear the deck's current chat thread (the "New chat" action). */
  clearChat: (deckId: string): Promise<void> =>
    httpAuth<void>(`/api/decks/${encodeURIComponent(deckId)}/chats`, { method: 'DELETE' }),
  /** Record a thumbs-up / thumbs-down on one assistant message so the team can
   *  review bad responses. Re-rating the same `messageRef` upserts server-side.
   *  204 No Content on success → void. Callers fire-and-forget (a failed POST
   *  must never break the chat). */
  rateMessage: (deckId: string, body: MessageFeedbackRequest): Promise<void> =>
    httpAuth<void>(`/api/decks/${encodeURIComponent(deckId)}/feedback`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  // ── V2 library folders ────────────────────────────────────────────────
  listFolders: (): Promise<Folder[]> => httpAuth<Folder[]>('/api/folders'),
  createFolder: (name: string, icon?: string): Promise<Folder> =>
    httpAuth<Folder>('/api/folders', { method: 'POST', body: JSON.stringify({ name, icon }) }),
  renameFolder: (id: string, name: string, icon?: string): Promise<Folder> =>
    httpAuth<Folder>(`/api/folders/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ name, icon }) }),
  deleteFolder: (id: string): Promise<void> =>
    httpAuth<void>(`/api/folders/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  moveDeck: (deckId: string, folderId: string): Promise<void> =>
    httpAuth<void>(`/api/decks/${encodeURIComponent(deckId)}/move`, { method: 'POST', body: JSON.stringify({ folderId }) }),
  /** Mint a share. ttl defaults to '30d' for back-compat with the V1 callers
   *  that pre-date the TTL chip UI. Sharing v2 threads through an optional
   *  passcode (verbatim) or a generatePasscode flag — only sent when set so the
   *  wire body for an unprotected share stays `{"ttl": "<value>"}`. The response
   *  carries the one-time plaintext `passcode` when the share is protected. */
  createShare: (id: string, ttl: ShareTtl = '30d', opts?: CreateShareOptions): Promise<Share> => {
    const body: { ttl: ShareTtl; passcode?: string; generatePasscode?: boolean } = { ttl };
    if (opts?.passcode) body.passcode = opts.passcode;
    if (opts?.generatePasscode) body.generatePasscode = true;
    return httpAuth<Share>(`/api/decks/${encodeURIComponent(id)}/share`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /** List active shares for a deck (owner-only). */
  listShares:  (deckId: string): Promise<ShareSummary[]> =>
    httpAuth<ShareSummary[]>(`/api/decks/${encodeURIComponent(deckId)}/shares`),
  /** Revoke a share by token (owner-only). 204 No Content on success; the
   *  helper returns void rather than the empty body. */
  deleteShare: (deckId: string, token: string): Promise<void> =>
    httpAuth<void>(
      `/api/decks/${encodeURIComponent(deckId)}/shares/${encodeURIComponent(token)}`,
      { method: 'DELETE' },
    ),
  /** Resolve a share token. Returns a discriminated result: either the deck
   *  payload, or `{ requiresPasscode: true, title }` (the server returns this
   *  with HTTP 200 for a protected share the caller hasn't verified). A wrong /
   *  missing passcode at the API level still surfaces as an HttpError 401 — the
   *  page handles both shapes. */
  getShare:    (token: string): Promise<ShareResult> =>
    httpAnon<ShareResult>(`/api/shares/${encodeURIComponent(token)}`),
  /** Verify a passcode for a protected share. Returns the deck on success;
   *  throws HttpError 401 on a wrong passcode. */
  verifyShare: (token: string, passcode: string): Promise<SharedDeck> =>
    httpAnon<SharedDeck>(`/api/shares/${encodeURIComponent(token)}/verify`, {
      method: 'POST',
      body: JSON.stringify({ passcode }),
    }),
  /** Record an analytics event from the share view. FIRE-AND-FORGET: errors are
   *  swallowed so a failed beacon never blocks or breaks the viewer. */
  recordShareEvent: (token: string, ev: ShareEvent): Promise<void> =>
    httpAnon<void>(`/api/shares/${encodeURIComponent(token)}/events`, {
      method: 'POST',
      body: JSON.stringify(ev),
    }).then(
      () => undefined,
      () => undefined,
    ),
  /** Fetch the owner-facing analytics roll-up for a deck (owner-only). */
  getDeckAnalytics: (id: string): Promise<DeckAnalytics> =>
    httpAuth<DeckAnalytics>(`/api/decks/${encodeURIComponent(id)}/analytics`),
  /** Ask the server to generate an image from a subject (+ optional style /
   *  theme / aspect). The server calls Vertex Imagen and returns `{ id, src }`
   *  where `src` is a root-relative "/api/images/<id>" URL. AUTH required —
   *  uses the same Bearer-token wrapper as the deck routes. Optional fields are
   *  forwarded only when set so the first-cut wire body stays `{"subject": …}`;
   *  the server defaults each absent enum. A 503 (image-gen unconfigured) and a
   *  400 (empty subject) both surface as an {@link HttpError} the caller branches
   *  on by `.status`. */
  generateImage: (req: GenerateImageRequest): Promise<GenerateImageResponse> => {
    const body: GenerateImageRequest = { subject: req.subject };
    if (req.style !== undefined) body.style = req.style;
    if (req.theme !== undefined) body.theme = req.theme;
    if (req.aspect !== undefined) body.aspect = req.aspect;
    if (req.styleText !== undefined) body.styleText = req.styleText;
    return httpAuth<GenerateImageResponse>('/api/images/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** List the signed-in owner's generated images (newest-first). Returns
   *  metadata only (id/src/subject/style/theme/createdAt) — never the raw image
   *  bytes; thumbnails lazy-load from the public `GET /api/images/<id>` URL in
   *  each row's `src`. Owner-scoped via the Bearer-token wrapper. */
  listImages: (): Promise<GalleryImage[]> =>
    httpAuth<GalleryImage[]>('/api/images'),
  /** Soft-hide an image (owner-only). The bytes keep being served at
   *  `GET /api/images/<id>` so already-shared decks never 404; it just drops
   *  from the gallery list. 204 No Content on success → void. */
  hideImage: (id: string): Promise<void> =>
    httpAuth<void>(`/api/images/${encodeURIComponent(id)}/hide`, {
      method: 'POST',
    }),
  /** Ask the AI to write or improve an image prompt. `auto` writes a subject
   *  from the slide + deck + theme context; `enhance` improves the supplied
   *  draft. Returns the suggested prompt text (which only ever feeds the Imagen
   *  prompt, never HTML). Optional context fields are forwarded only when set. */
  imagePrompt: async (req: ImagePromptRequest): Promise<string> => {
    const body: ImagePromptRequest = { mode: req.mode };
    if (req.slide !== undefined) body.slide = req.slide;
    if (req.deck !== undefined) body.deck = req.deck;
    if (req.theme !== undefined) body.theme = req.theme;
    if (req.draft !== undefined) body.draft = req.draft;
    const res = await httpAuth<ImagePromptResponse>('/api/decks/image-prompt', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return res.prompt;
  },
  /** Apply a free-text AI edit to ONE slide. Sends the focused slide's raw JSON
   *  plus an instruction and returns the edited slide's raw JSON string; the
   *  caller patches only that slide (never the whole deck). */
  editSlide: async (slide: unknown, instruction: string): Promise<unknown> => {
    // `slide` is the slide OBJECT, not a stringified string — the server's
    // json.RawMessage must receive a JSON object; a double-encode here makes
    // it a quoted string that fails unmarshal (→ 502).
    const res = await httpAuth<{ slide: unknown }>('/api/decks/edit-slide', {
      method: 'POST', body: JSON.stringify({ slide, instruction }),
    });
    return res.slide;
  },

  /** Run ONE advisory, batched "quick visual" review over a generated deck and
   *  return the per-slide VISUAL/legibility issues it found (empty when the deck
   *  looks fine). ADVISORY: the server soft-fails a broken review to an empty
   *  list, so this never breaks the generation flow; the caller applies a bounded
   *  number of the issues as single-slide edits behind a feature flag. */
  visualCheck: async (deck: unknown): Promise<VisualIssue[]> => {
    const res = await httpAuth<VisualCheckResponse>('/api/decks/visual-check', {
      method: 'POST', body: JSON.stringify({ deck }),
    });
    return res.issues;
  },

  /** Evaluation-harness runs (internal dashboard). `listEvalRuns` returns
   *  newest-first summaries (no per-case data); `getEvalRun` returns one run
   *  WITH its full `data` payload. Both require auth. */
  listEvalRuns: (): Promise<EvalRun[]> => httpAuth<EvalRun[]>('/api/eval/runs'),
  getEvalRun: (id: string): Promise<EvalRun> =>
    httpAuth<EvalRun>(`/api/eval/runs/${encodeURIComponent(id)}`),
};
